import React, { useEffect, useMemo, useRef, useState } from "react";
import * as d3 from "d3";
import { useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import { Category, DataPoint } from "fileParsing/types";
import Tooltip from "containers/Components/Tooltip";
import { useAppDispatch, useAppSelector } from "index";
import { ResizeComponent, SizeProps } from "containers/hooks/ResizeComponent";
import { useDatapointsFromExportId } from "containers/hooks/useData";
import { TimelineRangeType } from "@store/filters/types";
import { getMinMax } from "@utils/date";
import { setTimelineSelection } from "@store/filters/actions";
import { ApplicationState } from "@store/index";
import { OptionKey } from "@store/options/types";

export type TimelineSettings = {
  height: number;
  paddingBottom: number;
  paddingLeft: number;
  paddingRight: number;
  paddingTop: number;
  precision: number;
  private: boolean;
  timePadding: number;
  tooltipWidth: number;
  mouseSelectRadius: number;
  scaleCircleSize: boolean;
  width: number;
};

const defaultSettings: TimelineSettings = {
  width: 1300,
  height: 800,

  paddingRight: 20,
  paddingTop: 50,
  paddingLeft: 60,
  paddingBottom: 8,
  timePadding: 0.01,
  tooltipWidth: 400,
  mouseSelectRadius: 15,
  scaleCircleSize: true,

  precision: 8,
  private: false,
};

// Component-specific props.
type ComponentProps = {
  exportId?: number;
  settings?: TimelineSettings;
  yLabelSteps?: number;
};

/**
 * A plot using Cartesian coordinates to display two time-related variables.
 * While time is one dimensional, the repetitive cycles are considered and
 * split into two dimensions. The x-axis is showing years and months over a
 * longer time-period, while the y-axis is showing a single day over a 24
 * hours period. This provides a familiar way to relate to the visualization.
 * A grid allows for better orientation and comparability. Each circle in
 * the visualization represents a data point which is colored according to
 * its category. Category.Other reduce overplotting, only a border of the circle is drawn.
 *
 *
 * @param props see TimelineSettings
 * @returns React.FC rendering
 */
function Timeline(props: ComponentProps & SizeProps) {
  const theme = useTheme();
  const dispatch = useAppDispatch();

  const colorCanvasRef = useRef<HTMLCanvasElement>(null);
  const interactCanvasRef = useRef<HTMLCanvasElement>(null);
  const xaxisRef = useRef<SVGGElement>(null);
  const yaxisRef = useRef<SVGGElement>(null);
  const gridsRef = useRef<SVGGElement>(null);
  const nodecolor = useAppSelector((state) => state.options.options[OptionKey.ColorScheme].value);

  const themeType = useAppSelector((state) => state.applicationLayout.type);
  const selection = useAppSelector((state: ApplicationState) => state.filters.selection);

  const settings = {
    ...defaultSettings,
    width: props.size.width,
    height: props.size.height,
  };
  const canvasWidth = Math.floor(settings.width - settings.paddingRight - settings.paddingLeft);
  const canvasHeight = Math.floor(settings.height - settings.paddingTop - settings.paddingBottom);

  const data: DataPoint[] = useDatapointsFromExportId(props.exportId, false);

  const categories = React.useRef<{ [label: string]: string }>({});
  //const [size, setSize] = useState(1 << 14);
  const drawSize = 1 << 14;
  const animReq = React.useRef<number | undefined>();
  const findReq = React.useRef<number | undefined>();

  const [zoom, setZoom] = useState({
    k: 1,
    x: 0,
    y: 0,
  });

  const [tooltip, setTooltip] = useState({
    visible: false,
    category: Category.Other,
    subcat: "",
    date: "",
    text: "",
    uri: "",
    isFixed: false,
    id: -1,
    position: { x: 0, y: 0 },
    dataPoint: {} as DataPoint,
  });

  //const d3ScaleX = useRef<d3.ScaleTime<number, number, never> | null>(null);
  //const d3ScaleY = useRef<d3.ScaleTime<number, number, never> | null>(null);
  const { xScale, yScale, xAxis, yAxis } = useMemo(
    () => getScales(),
    [data.length, zoom.k, zoom.x, zoom.y, settings.timePadding, canvasHeight, canvasWidth],
  );

  // Making sure constant function also have access to xScale
  const xScaleRef = React.useRef(xScale);

  useEffect(() => {
    xScaleRef.current = xScale;
  }, [xScale]);

  useEffect(() => {
    const minMax = getMinMax(data);

    if (!selection?.[0] || !selection?.[1] || !minMax?.[0] || !minMax?.[1]) return;

    const init = initalTimeframe();
    const selectDist = selection[1].getTime() - selection[0].getTime();
    const scale = (init.timeEnd - init.timeStart + 2 * init.timePadding) / selectDist;

    const canvasScale = canvasWidth / selectDist;
    const shift = -(selection[0].getTime() - (init.timeStart - init.timePadding)) * canvasScale;

    setZoom({
      x: shift,
      y: 0,
      k: scale,
    });

    if (interactCanvasRef.current) {
      const { zoom } = getZoom();
      zoom.transform(d3.select(interactCanvasRef.current), new d3.ZoomTransform(scale, shift, 0));
      d3.select(interactCanvasRef.current).call(zoom);
    }
  }, [data.length, selection, canvasWidth]);

  const nodeSizeScale: d3.ScaleLinear<number, number, never> = useMemo(() => {
    const maxTextSize = d3.max(data, (d) => d.text.length) || 5;
    const textScale = d3.scaleLinear().domain([0, maxTextSize]).range([2.5, 15]);
    return textScale;
  }, [data]);

  // This useEffect block is run only once after the component mounts.
  useEffect(() => {
    // Attach zoom listener
    if (interactCanvasRef.current) {
      const { zoom } = getZoom();
      d3.select(interactCanvasRef.current).call(zoom);
    }
  }, []);

  // This useEffect is run every time the data or component sizes change.
  useEffect(() => {
    renderXAxis(xaxisRef.current);
    renderYAxis(yaxisRef.current);
    renderGrids(gridsRef.current);
    renderChart();

    return () => {
      if (animReq.current) window.cancelAnimationFrame(animReq.current);
    };
  }, [
    data.length,
    zoom.k,
    zoom.x,
    zoom.y,
    nodecolor,
    settings.timePadding,
    settings.precision,
    settings.scaleCircleSize,
    canvasHeight,
    canvasWidth,
    themeType,
  ]);

  // -----------------------------------------------------------------------------------------------------
  // Functions
  // -----------------------------------------------------------------------------------------------------
  function getZoom() {
    const beginning = data.length > 0 ? data[0].date.getTime() : 0;
    const ending = data.length > 0 ? data[data.length - 1].date.getTime() : 0;
    const day = 24 * 60 * 60 * 1000;

    const zoom = d3
      .zoom<HTMLCanvasElement, unknown>()
      .scaleExtent([1, Math.max(100, (ending - beginning) / day)])
      .extent([
        [0, 0],
        [canvasWidth, canvasHeight],
      ])
      .translateExtent([
        [0, -Infinity],
        [canvasWidth, Infinity],
      ])
      .on("zoom", (e) => {
        if (e.sourceEvent === null) return;

        setZoom({
          k: e.transform.k,
          x: e.transform.x,
          y: 0,
        });
      })
      .on("end", (e) => {
        if (e.sourceEvent === null) return;

        const newSelection: TimelineRangeType = [xScaleRef.current.domain()[0], xScaleRef.current.domain()[1]];
        const minMax = getMinMax(data);

        if (newSelection?.[0] && newSelection?.[1] && minMax?.[0] && minMax?.[1]) {
          if (newSelection[0] < minMax[0] && newSelection[1] > minMax[1]) {
            newSelection[0] = minMax[0];
            newSelection[1] = minMax[1];
          } else {
            if (newSelection[0] < minMax[0]) {
              const diff = minMax[0].getTime() - newSelection[0].getTime();
              newSelection[0] = minMax[0];
              newSelection[1] = new Date(newSelection[1].getTime() + diff);
            }
            if (newSelection[1] > minMax[1]) {
              const diff = newSelection[1].getTime() - minMax[1].getTime();
              newSelection[0] = new Date(newSelection[0].getTime() - diff);
              newSelection[1] = minMax[1];
            }
          }
        }
        dispatch(setTimelineSelection(newSelection));
      });

    return { zoom };
  }

  function initalTimeframe() {
    const timeStart = data.length > 0 ? data[0].date.getTime() : 0;
    const timeEnd = data.length > 0 ? data[data.length - 1].date.getTime() : 0;
    const timePadding = (timeEnd - timeStart) * settings.timePadding;
    return { timeStart, timeEnd, timePadding };
  }

  function getScales() {
    const { timeStart, timeEnd, timePadding } = initalTimeframe();
    const day = 24 * 60 * 60 * 1000;
    const month = day * 31;

    const xScale = d3.zoomIdentity
      .translate(zoom.x, zoom.y)
      .scale(zoom.k)
      .rescaleX(
        d3
          .scaleTime()
          .domain([new Date(timeStart - timePadding), new Date(timeEnd + timePadding)])
          .range([0, canvasWidth]),
      );

    const yScale = d3
      .scaleTime()
      .domain([new Date(0), new Date(0 + day)])
      .range([0, canvasHeight - 12]);

    const yAxis = d3
      .axisLeft<Date>(yScale)
      //.tickFormat(d3.timeFormat("%H:%M"))
      .tickFormat((_, i) => (i % (props.yLabelSteps ?? 2) === 0 ? `${String(i).padStart(2, "0")}:00` : ``))
      .tickArguments([d3.timeHour.every(1)])
      .tickSize(24);

    const xAxis = d3.axisTop(xScale);
    xAxis.tickSize(12);

    const diff = xScale.domain()[1].getTime() - xScale.domain()[0].getTime();
    xAxis.tickArguments(diff > month ? [] : [d3.timeDay.every(1)]);

    return {
      xScale,
      yScale,
      xAxis,
      yAxis,
    };
  }

  function dateToXScale(d: Date) {
    return xScaleRef.current(dateToLocalTime(d) - (dateToLocalTime(d) % 86400000) + 43200000);
  }

  function dateToYScale(d: Date) {
    return yScale(dateToLocalTime(d) % 86400000);
  }

  function dateToLocalTime(d: Date): number {
    return d.getTime() - d.getTimezoneOffset() * 60000;
  }

  function getColor() {
    switch (nodecolor) {
      case "location":
        return (d: DataPoint) => getPositionColor(d.text);
      case "category":
        return (d: DataPoint) => theme.palette.categories[d.category].main;
      case "subcategory":
        return (d: DataPoint) => getRandomColorFromMap(d.subcategory); //settings.subcolormap[d.subcategory];
      case "provider":
        return (d: DataPoint) => getProviderColorFromSubcategory(d.subcategory.charAt(0));
      case "solid":
      default:
        return () => "#5FAF77";
    }
  }

  function getProviderColorFromSubcategory(str: string) {
    // TODO: refactor this to some better place
    if (str === "F") return "#4267B2";
    if (str === "T") return "#1DA1F2";
    if (str === "G") return "#0F9D58";
    if (str === "I") return "#E1306C";
    if (str === "L") return "#0A66C2";
    if (str === "N") return "#E50914";
    return "#000";
  }

  function getPositionColor(name: string): string {
    if (!name) return "#333";

    // parses "(498275804, 85011349)" to "49,8" based on precision
    // parses "(49.8275804, 8.5011349)" to "49,8" based on precision
    const c = name.indexOf(",");
    const p = name.indexOf(")");
    const o = name.indexOf("(");
    if (o !== 0 || c === -1 || p === -1) return "#333";
    const precision = settings.precision || 7;
    const lat = name.substring(1, c - 1 - precision); // -90 to 90
    const long = name.substring(c + 2, p - c - 2 - precision); //-180 to 180
    const id = lat + "," + long;

    return getRandomColorFromMap(id);
  }

  function getRandomColorFromMap(key: string): string {
    if (!categories.current[key]) {
      //let color = d3.lab(70, -100 + 200 * Math.random(), -100 +200*Math.random());
      //categories.current[key] = d3.interpolateTurbo((counter%10)/10);
      const color = "hsl(" + Math.random() * 360 + ",66%,66%)";
      categories.current[key] = color;
      //setCounter((counter || 0) + 1);
    }
    return categories.current[key];
  }

  function getTransition() {
    return d3.transition().duration(0).ease(d3.easeLinear);
  }

  // -----------------------------------------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------------------------------------

  function renderXAxis(node: SVGGElement | null) {
    const dataJoin = d3.select(node).selectAll<SVGGElement, unknown>(".xAxis").data([{ xAxis }]);

    const dataEnter = dataJoin
      .enter()
      .append("g")
      .attr("class", "xAxis")
      .attr("stroke-width", "0px")
      .attr("width", canvasWidth)
      .attr("height", 50);

    dataEnter.merge(dataJoin).transition(getTransition()).call(xAxis);

    dataEnter
      .merge(dataJoin)
      .selectAll("text")
      .attr("font-size", "min(2.5vw, 2em)")
      .attr("transform", "translate(0,0)")
      .on("click", () => {
        // TODO: Add onclick zoom
      });

    dataJoin.exit().remove();
  }

  function renderYAxis(node: SVGGElement | null) {
    const dataJoin = d3.select(node).selectAll<SVGGElement, unknown>(".yAxis").data([{ yAxis }]);

    const dataEnter = dataJoin
      .enter()
      .append("g")
      .attr("class", "yAxis")
      .attr("stroke-width", "0px")
      .attr("height", canvasHeight)
      .attr("width", settings.paddingLeft);

    dataEnter.merge(dataJoin).attr("transform", "translate(5,0)").transition(getTransition()).call(yAxis);

    dataEnter
      .merge(dataJoin)
      .selectAll<SVGTextElement, unknown>("text")
      .attr("font-size", "1.5em")
      .attr("transform", "translate(10,5)");

    dataJoin.exit().remove();
  }

  function renderGrids(node: SVGGElement | null) {
    const dataJoin = d3.select(node).selectAll<SVGGElement, unknown>(".xGrid").data([{ xAxis }]);

    const dataEnter = dataJoin
      .enter()
      .append("g")
      .attr("class", "xGrid")
      .attr("stroke-width", "1px")
      .attr("color", theme.palette.action.selected);

    dataEnter
      .merge(dataJoin)
      //.attr('transform', 'translate(5,5)')
      .transition(getTransition())
      .call(xAxis.tickSize(-canvasHeight).tickFormat(() => ""));

    dataJoin.exit().remove();

    const dataJoin2 = d3.select(node).selectAll<SVGGElement, unknown>(".yGrid").data([{ yAxis }]);

    const dataEnter2 = dataJoin2
      .enter()
      .append("g")
      .attr("class", "yGrid")
      .attr("stroke-width", "1px")
      .attr("color", theme.palette.action.selected);

    dataEnter2
      .merge(dataJoin2)
      //.attr('transform', 'translate(5,5)')
      .transition(getTransition())
      .call(
        yAxis
          .ticks(9)
          .tickSize(-canvasWidth)
          .tickFormat(() => ""),
      );

    dataJoin2.exit().remove();
  }

  function renderChart() {
    if (colorCanvasRef.current) {
      const ctx = colorCanvasRef.current.getContext("2d");
      if (ctx) {
        if (animReq.current) {
          // clear current animation
          window.cancelAnimationFrame(animReq.current);
        }
        animReq.current = window.requestAnimationFrame(() => chunkNextData(ctx, 0, 0));
      }
    }
  }

  function chunkNextData(context: CanvasRenderingContext2D, progress: number, progressEnd: number) {
    if (progress === 0) {
      // emtpy drawing area
      context.clearRect(0, 0, settings.width, settings.height);

      // figure out how many items to draw
      progressEnd = 0;
      const start = xScaleRef.current.domain()[0];
      const end = xScaleRef.current.domain()[1];
      let progressSet = false;
      for (let i = 0; i < data.length; ++i) {
        if (!progressSet && data[i].date >= start) {
          progress = i;
          progressSet = true;
        }
        if (data[i].date >= end) {
          progressEnd = i;
          break;
        }
      }
      if (progressEnd === 0) {
        progressEnd = data.length;
      }
    }
    //const startTime = performance.now();
    drawCircles(context, progress, Math.min(data.length - 1, progress + drawSize));
    //const duration = performance.now() - startTime;

    //if (duration > 50) {
    //setSize(Math.max(size - (1 << 12), 1 << 12));
    //console.log("[Timeline] Performance duration took: ", Math.floor(duration), " ms with", size);
    //}

    if (progress + drawSize < progressEnd) {
      animReq.current = window.requestAnimationFrame(() => chunkNextData(context, progress + drawSize, progressEnd));
    } else {
      animReq.current = undefined;
    }
  }

  function drawCircles(context: CanvasRenderingContext2D, start: number, end: number) {
    context.save();
    context.translate(0, 0);
    context.lineWidth = 1;
    const tau = 2 * Math.PI;
    const color = getColor();
    //context.globalAlpha = 1;

    /*
    // faster drawing, but doesnt work for all use cases yet
    Object.entries(Category).forEach(([name, category]) => {
      context.beginPath();
      let arr = data
        .slice(start, end)
        .filter(d => d.category === category);

      if(arr.length > 0){
        context.strokeStyle = color(arr[0]);
        context.fillStyle = color(arr[0]) + "11";
      }

      for(let item of arr){
        context.arc(
          dateToXScale(item.date),
          dateToYScale(item.date),
          size, 0, tau);
      }
      context.fill();
      context.stroke();
    });
    */
    for (let i = start; i < end; i++) {
      context.beginPath();
      context.strokeStyle = color(data[i]);
      context.fillStyle = color(data[i]) + "11";
      context.arc(
        dateToXScale(data[i].date),
        dateToYScale(data[i].date),
        settings.scaleCircleSize ? nodeSizeScale(data[i].text.length) : 4,
        0,
        tau,
      );
      context.fill();
      context.stroke();
    }
    context.restore();
  }

  /*
  // TODO: maybe for a later release with selection of neighbor datapoints
  function setPositionTooltip(i: number) {
    if (i > data.length) {
      i = i - 1;
    } else if (i < 0) {
      i = i + 1;
    }
    const ix = dateToXScale(data[i].date);
    const iy = dateToYScale(data[i].date);
    updateTooltip(ix, iy, data[i], i);
  }
  */

  function mouseClickListener() {
    const newTooltip = {
      ...tooltip,
      isFixed: !tooltip.isFixed,
    };
    setTooltip(newTooltip);
  }

  function mouseMoveListener(event: React.MouseEvent<HTMLCanvasElement, MouseEvent>) {
    const [mouseX, mouseY] = d3.pointer(event);

    if (tooltip.isFixed) {
      return;
    }
    if (findReq.current) {
      // clear current animation
      window.cancelAnimationFrame(findReq.current);
    }

    findReq.current = window.requestAnimationFrame(() => {
      setTooltip((prevTooltipState) => {
        let newTooltipState = prevTooltipState;
        const [res, pos] = findNearestPoint(mouseX, mouseY, settings.mouseSelectRadius);
        if (res !== undefined) {
          // Show tooltip
          document.body.style.cursor = "pointer";

          newTooltipState = {
            ...newTooltipState,
            category: res.category,
            visible: true,
            subcat: res.subcategory,
            date: res.date.toLocaleString(),
            text: res.text,
            uri: res.uri ? res.uri : "",
            position: {
              x: Math.min(event.pageX, window.innerWidth),
              y: event.pageY,
            },
            id: pos,
            dataPoint: res,
          };

          const tmp = res;
          drawMouse(
            dateToXScale(tmp.date),
            dateToYScale(tmp.date),
            settings.scaleCircleSize ? nodeSizeScale(tmp.text.length) : 4,
            theme.neutral.main,
            getColor()(tmp),
          );
        } else {
          if (prevTooltipState.visible) {
            // Hide tooltip
            document.body.style.cursor = "auto";
            newTooltipState = {
              ...newTooltipState,
              visible: false,
            };
          }
          drawMouse(mouseX, mouseY, settings.mouseSelectRadius, theme.neutral.main, "");
        }
        return newTooltipState;
      });
    });
  }

  function findNearestPoint(x: number, y: number, radius: number): [DataPoint | undefined, number] {
    let r = radius * radius;
    // we get the approximate x position because the data is spread over the x axis
    // then we pad the data left and right
    const leftday = xScaleRef.current.invert(x - r);
    const rightday = xScaleRef.current.invert(x + r);
    let i = d3.bisector((a: DataPoint, b: Date) => a.date.getTime() - b.getTime()).left(data, leftday);
    let closest,
      dx,
      dy,
      d2,
      ix,
      iy,
      pos = -1;

    const n = d3.bisector((a: DataPoint, b: Date) => a.date.getTime() - b.getTime()).right(data, rightday);

    // iterate over nearby data to find perfect match
    for (; i < n; ++i) {
      ix = dateToXScale(data[i].date);
      iy = dateToYScale(data[i].date);
      // bounding box cutting
      if (ix - r >= x || ix + r <= x || iy - r >= y || iy + r <= y) continue;
      dx = x - ix;
      dy = y - iy;
      d2 = dx * dx + dy * dy;
      if (d2 < r) {
        closest = data[i];
        pos = i;
        r = d2;
      }
    }
    return [closest, pos];
  }

  function drawMouse(x: number, y: number, radius: number, color: string, color2: string) {
    const context = interactCanvasRef?.current?.getContext("2d");
    if (context) {
      context.clearRect(0, 0, canvasWidth, canvasHeight);
      context.save();
      context.translate(0, 0);

      context.globalAlpha = 1;

      // Nodes
      context.beginPath();
      if (color2 !== "") {
        context.fillStyle = "#ffffffaa";
        context.arc(x, y, settings.mouseSelectRadius, 0, 2 * Math.PI);
        context.fill();
        context.beginPath();
        context.fillStyle = color2;
        context.arc(x, y, radius, 0, 2 * Math.PI);
        context.fill();
      } else {
        context.fillStyle = color;
        context.arc(x, y, radius, 0, 2 * Math.PI);
        context.fill();
      }
      context.restore();
    }
  }

  // -----------------------------------------------------------------------------------------------------
  // Return
  // -----------------------------------------------------------------------------------------------------

  return (
    <Box
      onMouseLeave={() => {
        document.body.style.cursor = "auto";
        setTooltip((prevTooltipState) => {
          if (!prevTooltipState.isFixed) {
            drawMouse(0, 0, settings.mouseSelectRadius, theme.neutral.main, "");
            return {
              ...prevTooltipState,
              visible: false,
            };
          }
          return prevTooltipState;
        });
      }}
    >
      <div style={{ position: "relative" }}>
        <canvas
          ref={colorCanvasRef}
          height={canvasHeight}
          width={canvasWidth}
          style={{
            transform: `translate(${settings.paddingLeft}px,${settings.paddingTop}px)`,
            pointerEvents: "none",
            position: "absolute",
          }}
        />
        <canvas
          ref={interactCanvasRef}
          height={canvasHeight}
          width={canvasWidth}
          style={{
            transform: `translate(${settings.paddingLeft}px,${settings.paddingTop}px)`,
            position: "absolute",
          }}
          onClick={mouseClickListener}
          onMouseMove={mouseMoveListener}
          onMouseLeave={() => {
            drawMouse(0, 0, 0, theme.neutral.main, "");
            document.body.style.cursor = "auto";
          }}
        />
      </div>
      <svg
        style={{
          height: settings.height,
          width: settings.width,
        }}
      >
        <g transform={`translate(${settings.paddingLeft},${settings.paddingTop})`}>
          <rect
            color={theme.palette.action.selected}
            width={canvasWidth}
            height={canvasHeight}
            fill="transparent"
            stroke={theme.palette.action.selected}
            strokeWidth="1px"
          />
        </g>
        <g transform={`translate(${settings.paddingLeft},${settings.paddingTop})`} ref={xaxisRef}></g>
        <g transform={`translate(${settings.paddingLeft},${settings.paddingTop})`} ref={yaxisRef}></g>
        <g transform={`translate(${settings.paddingLeft},${settings.paddingTop})`} ref={gridsRef}></g>
      </svg>

      <Tooltip
        visible={tooltip.visible}
        position={tooltip.position}
        settings={{ tooltipWidth: settings.tooltipWidth, margin: 0 }}
        texts={{
          title: tooltip.category,
          subtitle: tooltip.subcat,
          date: tooltip.date,
          body: tooltip.text,
        }}
        dataPoint={tooltip.dataPoint}
        uri={tooltip.uri === "" ? undefined : tooltip.uri}
        category={tooltip.category}
        isFixed={tooltip.isFixed}
        /*
      // TODO: Maybe for another release
      clickToNext={() => {
        setPositionTooltip(tooltip.id + 1);
      }}
      clickToPrev={() => {
        setPositionTooltip(tooltip.id - 1);
    }}
    */
      />
    </Box>
  );
}

export default ResizeComponent(Timeline);
